/**
 * The queue's columns and the axes it can be sorted on.
 *
 * Its own module because a file exporting both a component and a constant
 * breaks Fast Refresh, and that rule is enforced here.
 */

/**
 * Columns the reader may hide. `title` and `status` are not offered.
 *
 * Which of these a level actually shows is `level.ts` — a company does not need
 * its own name in every row of its own queue.
 */
export const QUEUE_COLUMNS = [
  { key: "reference", label: "Ref" },
  // The source sits by the reference: both say what the row *is* before the
  // title says what it is about, and a glyph reads fastest at the edge.
  { key: "source", label: "Source" },
  // "Company", never "client". Nothing in this product is a client: the row is
  // an organization, and the word for it is the one the org model uses.
  { key: "organization", label: "Company" },
  { key: "priority", label: "Priority" },
  { key: "predicted", label: "Predicted" },
  { key: "risk", label: "Risk" },
  { key: "assignee", label: "Assignee" },
  // The SLA clock, as a due moment. "Due" rather than "SLA": the reader wants
  // to know when, not which promise the clock is measuring — that is the hover.
  { key: "sla", label: "Due" },
  { key: "age", label: "Age" },
  { key: "in_status", label: "In status" },
] as const;

export type QueueColumn = (typeof QUEUE_COLUMNS)[number]["key"];

/**
 * Columns the server can order on: every visible one. Sorting is always the
 * server's, because ordering the current page in the client reads as a sort
 * and is not one.
 */
export type QueueSortKey =
  | "created"
  | "reference"
  | "priority"
  | "status"
  | "organization"
  | "source_key"
  | "risk_level"
  | "status_changed_at"
  | "title"
  | "estimated_saving_low"
  | "assignee"
  | "sla_due_at";

/** Every sort key, for the URL allow-list. */
export const QUEUE_SORT_KEYS: readonly QueueSortKey[] = [
  "created",
  "reference",
  "priority",
  "status",
  "organization",
  "source_key",
  "risk_level",
  "status_changed_at",
  "title",
  "estimated_saving_low",
  "assignee",
  "sla_due_at",
];
